import { useEffect, useRef, useState } from 'react';
import { DEMORALIZING_WARCRY_SOUND_PATH, DEMORALIZING_WARCRY_EFFECT_DURATION_MS } from '../config/demoralizingWarcryEffect';
import { OBLIVION_PULSE_SOUND_PATH } from '../config/oblivionPulseEffect';

/** Strategem IDs that use the fullscreen ripple (Demoralizing Warcry blue; Oblivion Pulse gray). */
const SHOCKWAVE_STRATEGEM_IDS = new Set([113, 325]);

/**
 * Hook to detect strategems that trigger the fullscreen shockwave (113 Demoralizing Warcry, 325 Oblivion Pulse).
 * @param {Object} gameState - Current game state
 * @param {string} userId - Current user's ID
 * @returns {Object} - { activeShockwaves, triggerShockwave }
 */
export const useStrategemShockwave = (gameState, userId) => {
  const [activeShockwaves, setActiveShockwaves] = useState([]);
  const processedStrategemsRef = useRef(new Set());
  const isFirstRenderRef = useRef(true);

  // Get center position where all 4 zones meet (opponent_front, opponent_flank, your_front, your_flank)
  const getZoneIntersectionCenter = () => {
    // Find all 4 zone elements
    const opponentFront = document.querySelector('.battle-zone.opponent-front');
    const opponentFlank = document.querySelector('.battle-zone.opponent-flank');
    const yourFront = document.querySelector('.battle-zone.front');
    const yourFlank = document.querySelector('.battle-zone.flank');
    
    // If we have all 4 zones, calculate the intersection point
    if (opponentFront && opponentFlank && yourFront && yourFlank) {
      const opponentFrontRect = opponentFront.getBoundingClientRect();
      const opponentFlankRect = opponentFlank.getBoundingClientRect();
      const yourFrontRect = yourFront.getBoundingClientRect();
      const yourFlankRect = yourFlank.getBoundingClientRect();
      
      // The center is where the zones meet:
      // - Horizontally: between opponent_front right edge and opponent_flank left edge (or vice versa)
      //   Actually, it's the midpoint between the two battlefield-containers
      // - Vertically: between opponent zones bottom and your zones top
      
      // Get the two battlefield-containers
      const battlefieldContainers = document.querySelectorAll('.battlefield-container');
      if (battlefieldContainers.length >= 2) {
        const opponentContainer = battlefieldContainers[0]; // Top container (opponent zones)
        const yourContainer = battlefieldContainers[1]; // Bottom container (your zones)
        
        const opponentRect = opponentContainer.getBoundingClientRect();
        const yourRect = yourContainer.getBoundingClientRect();
        
        // Horizontal center: middle of the containers (they should be aligned)
        const centerX = opponentRect.left + opponentRect.width / 2;
        
        // Vertical center: midpoint between opponent container bottom and your container top
        const centerY = (opponentRect.bottom + yourRect.top) / 2;
        
        return { x: centerX, y: centerY };
      }
      
      // Fallback: calculate from zone positions
      // Horizontal: midpoint between front and flank zones
      const horizontalCenter = (opponentFrontRect.right + opponentFlankRect.left) / 2;
      // Vertical: midpoint between opponent zones bottom and your zones top
      const verticalCenter = (opponentFrontRect.bottom + yourFrontRect.top) / 2;
      
      return { x: horizontalCenter, y: verticalCenter };
    }
    
    // Fallback: try to find any battlefield container
    const battlefieldElement = document.querySelector('.battlefield-container');
    if (battlefieldElement) {
      const rect = battlefieldElement.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
    }
    
    // Final fallback: screen center
    return {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2
    };
  };

  useEffect(() => {
    if (!gameState?.state || !userId) return;

    // Skip first render to avoid triggering on initial load
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    const allPlayerIds = Object.keys(gameState.state || {});

    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach(strategem => {
        if (!strategem || !SHOCKWAVE_STRATEGEM_IDS.has(strategem.id)) return;

        const strategemKey = `strategem-${strategem.id}-${strategem.instanceId}-${playerId}`;

        if (processedStrategemsRef.current.has(strategemKey)) {
          return;
        }

        processedStrategemsRef.current.add(strategemKey);

        const variant = strategem.id === 325 ? 'oblivionPulse' : 'demoralizing';
        const label =
          strategem.id === 325 ? '325 (Oblivion Pulse)' : '113 (Demoralizing Warcry)';
        console.log(`🌊 useStrategemShockwave: Detected strategem ${label}`, {
          strategemInstanceId: strategem.instanceId,
          playerId,
          isCurrentPlayer: String(playerId) === String(userId),
          variant,
        });

        const shockwavePosition = getZoneIntersectionCenter();

        const animationKey = `shockwave-${strategem.id}-${strategem.instanceId}-${Date.now()}`;
        setActiveShockwaves(prev => [
          ...prev,
          {
            key: animationKey,
            x: shockwavePosition.x,
            y: shockwavePosition.y,
            strategemInstanceId: strategem.instanceId,
            playerId,
            variant,
          },
        ]);

        try {
          const soundPath =
            strategem.id === 325 ? OBLIVION_PULSE_SOUND_PATH : DEMORALIZING_WARCRY_SOUND_PATH;
          const audio = new Audio(soundPath);
          audio.play().catch(() => {});
        } catch (_) {}

        setTimeout(() => {
          setActiveShockwaves(prev => prev.filter(sw => sw.key !== animationKey));
        }, DEMORALIZING_WARCRY_EFFECT_DURATION_MS + 120);
      });
    });

    // Clean up old processed strategems (keep last 50)
    if (processedStrategemsRef.current.size > 50) {
      const keys = Array.from(processedStrategemsRef.current);
      keys.slice(0, keys.length - 50).forEach(key => {
        processedStrategemsRef.current.delete(key);
      });
    }
  }, [gameState, userId]);

  const completeShockwave = (key) => {
    setActiveShockwaves(prev => prev.filter(sw => sw.key !== key));
  };

  return {
    activeShockwaves,
    completeShockwave
  };
};

export default useStrategemShockwave;
